/**
 * Registre des tuiles de BASE, communes à tous les jeux (concept §8.2).
 *
 * Le moteur ne connaît que `# . _ @ P M p m`. Tout autre caractère doit être
 * déclaré par le `tileLegend` du GameModule — c'est ainsi que `s`, `c`, `+`,
 * `-`, `!`, `~` resteront confinés à `@md/game-magnetic-duo`.
 */

/** Rôle particulier d'une tuile pour le parser (spawns et sorties). */
export type TileMarker = 'spawn-a' | 'spawn-b' | 'exit-a' | 'exit-b';

export interface TileDef {
  /** Caractère ASCII de la carte. */
  readonly char: string;
  /** Nom lisible (debug, éditeur, rapports du validateur). */
  readonly name: string;
  /** Bloque le déplacement dans les quatre directions. */
  readonly solid: boolean;
  /** Plateforme fine : bloque uniquement par le dessus. */
  readonly oneWay: boolean;
  /** Mortelle pour les deux personnages (pics, broyeurs). */
  readonly lethal: boolean;
  /** Marqueur consommé par le parser (spawn / sortie), sinon `null`. */
  readonly marker: TileMarker | null;
  /** Couleur indicative pour le rendu (0xRRGGBB), `null` = ne rien dessiner. */
  readonly tint: number | null;
}

function tile(def: Partial<TileDef> & Pick<TileDef, 'char' | 'name'>): TileDef {
  return {
    solid: false,
    oneWay: false,
    lethal: false,
    marker: null,
    tint: null,
    ...def,
  };
}

export const TILE_EMPTY = tile({ char: '.', name: 'vide' });
export const TILE_SOLID = tile({ char: '#', name: 'mur', solid: true, tint: 0x2b3450 });
export const TILE_PLATFORM = tile({
  char: '_',
  name: 'plateforme fine',
  oneWay: true,
  tint: 0x4a5a86,
});
export const TILE_SPIKE = tile({ char: '@', name: 'pics', lethal: true, tint: 0xb0b8cc });
export const TILE_SPAWN_A = tile({ char: 'P', name: 'spawn joueur A', marker: 'spawn-a' });
export const TILE_SPAWN_B = tile({ char: 'M', name: 'spawn joueur B', marker: 'spawn-b' });
export const TILE_EXIT_A = tile({
  char: 'p',
  name: 'sortie joueur A',
  marker: 'exit-a',
  tint: 0xff4d5e,
});
export const TILE_EXIT_B = tile({
  char: 'm',
  name: 'sortie joueur B',
  marker: 'exit-b',
  tint: 0x35c8ff,
});

/** Tuiles de base, indexées par caractère. */
export const BASE_TILES: Readonly<Record<string, TileDef>> = Object.freeze({
  [TILE_EMPTY.char]: TILE_EMPTY,
  [TILE_SOLID.char]: TILE_SOLID,
  [TILE_PLATFORM.char]: TILE_PLATFORM,
  [TILE_SPIKE.char]: TILE_SPIKE,
  [TILE_SPAWN_A.char]: TILE_SPAWN_A,
  [TILE_SPAWN_B.char]: TILE_SPAWN_B,
  [TILE_EXIT_A.char]: TILE_EXIT_A,
  [TILE_EXIT_B.char]: TILE_EXIT_B,
});

/** Tuile renvoyée hors des limites de la grille : du vide (on y tombe). */
export const TILE_OUT_OF_BOUNDS = tile({ char: ' ', name: 'hors carte' });

export class TileLegendError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TileLegendError';
  }
}

/**
 * Fusionne la légende d'un GameModule avec la base commune.
 *
 * Un module ne peut PAS redéfinir un caractère de base : cela garantit que le
 * sens de `#`, `@` ou `P` est identique dans tous les jeux.
 */
export function resolveTileLegend(
  moduleLegend: Readonly<Record<string, TileDef>> = {},
): Readonly<Record<string, TileDef>> {
  const merged: Record<string, TileDef> = { ...BASE_TILES };
  for (const [char, def] of Object.entries(moduleLegend)) {
    if (char in BASE_TILES) {
      throw new TileLegendError(
        `Le module tente de redéfinir la tuile de base "${char}" (${BASE_TILES[char]?.name}).`,
      );
    }
    if (char.length !== 1) {
      throw new TileLegendError(`Une tuile doit tenir sur 1 caractère, reçu "${char}".`);
    }
    merged[char] = def;
  }
  return Object.freeze(merged);
}
